import React from 'react';
import { useForm, useFieldArray, SubmitHandler } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';
import SubTitle from '../../../ui/SubTitle';
import InputField from '../../../form/InputField';
import SubmitButton from '../../../form/SubmitButton';
import { useUpdateResource } from '../../../../hooks/useUpdateResource';
import LoadingButton from '../../../form/LoadingButton';

type Language = {
  languageCode: string;
  name: string;
};

type Instruction = {
  languages: Language[];
};

type FormValues = {
  productId: number;
  instructions: Instruction[];
};

const InstructionsForm: React.FC = () => {
  const { t } = useTranslation();
  const { productId ,storeId} = useParams<{ productId: string ,storeId:string}>();
  const numericStoreId = storeId ? parseInt(storeId) : undefined;
  const numericProductId = productId ? parseInt(productId) : 0;

   const [searchParams] = useSearchParams();
    const backQuery = (searchParams.get('back'));
     const {loading,updateResource} = useUpdateResource();
      const navigate = useNavigate();

  const { control, register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    defaultValues: {
      productId: numericProductId,
      instructions: []
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'instructions'
  });

    const navigatedLink = numericStoreId ? `/admin/stores/${numericStoreId}/products/${productId}/variants` :`/store/products/${productId}/variants`;
    const backLink = numericStoreId ? `/admin/stores/${numericStoreId}/products/${numericProductId}` :`/store/products/${numericProductId}`

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    const endPoint = numericStoreId?`product-instruction?storeId=${numericStoreId}`:"product-instruction"
    updateResource(endPoint,{productId:data.productId,languages:data?.instructions[0]?.languages},numericStoreId?"admin":"store",{
      method:"POST",
      successMessage:t('createdSuccess'),
      navigateAfter:()=>backQuery==="true"?navigate(-1) :navigate(navigatedLink)}
    )
  };

  return (
    <div className="max-w-[800px]">
      <SubTitle title="add_instructions" />

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

        {fields.map((item, index) => (
          <div key={item.id} className="p-4 rounded flex items-start gap-2 border border-muted">

            {/* Name EN */}
            <div className="flex-1">
              <InputField
                placeholder={`${t('name_en')}`}
                register={register(`instructions.${index}.languages.1.name`, { required: t('required_field') })}
                error={errors?.instructions?.[index]?.languages?.[1]?.name}
              />
              <input type="hidden" {...register(`instructions.${index}.languages.1.languageCode`)} value="en" />
            </div>

            
            {/* Name AR */}
            <div className="flex-1">
              <InputField
                placeholder={`${t('name_ar')}`}
                register={register(`instructions.${index}.languages.0.name`, { required: t('required_field') })}
                error={errors?.instructions?.[index]?.languages?.[0]?.name}
              />
              <input type="hidden" {...register(`instructions.${index}.languages.0.languageCode`)} value="ar" />
            </div>

            {/* Delete Button */}
            <button
              type="button"
              onClick={() => remove(index)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500 hover:bg-red-600 text-white transition"
            >
              <Trash2 size={16} />
            </button>

          </div>
        ))}

        {/* Add Instruction Button */}
        <button
          type="button"
          onClick={() =>
            append({
              languages: [
                { languageCode: 'ar', name: '' },
                { languageCode: 'en', name: '' }
              ]
            })
          }
          className="w-full py-3 bg-[#FFFFFF] flex justify-center items-center rounded border-[1.5px] border-dashed border-[#EBF3F0]"
        >
          <Plus size={20} />
        </button>

        {
          loading
          ?
          <LoadingButton/>
          :
          <SubmitButton label={t('create')} />
        }
        {
          backQuery!=="true"
          ?
          <Link to={navigatedLink}
          className={`bg-Black text-white px-6 py-2 h-[44px] text-xl  mt-2 w-full block text-center
          transition rounded-[8px] capitalize font-semibold hover:bg-opacity-85`}>
          {t('skip')}</Link>
          :
          <Link to={backLink}
          className={`bg-Black text-white px-6 py-2 h-[44px] text-xl  mt-2 w-full block text-center
          transition rounded-[8px] capitalize font-semibold hover:bg-opacity-85`}>
          {t('back')}</Link>
        }
      </form>
    </div>
  );
};

export default InstructionsForm;
